<template>
  <div class="system-card shadow-2">
    <div class="system-info">
      <div class="system-info-header v1-grey-10-bg">
        <div class="qrw-display-xs bold" :class="`${uiModeTitleColor}-color`">
          {{ uiModeDisplayTitle }}
        </div>
        <div class="qrw-paragraph-md-sm v1-grey-6-color" v-if="!isShowingToggle">
          {{ $t("subscription_status_panel.motto_" + mode) }}
        </div>
        <div class="toggle-box" v-else>
          <div
            class="qrw-heading-xs bold w-100"
            :class="{
              'green-5-color': modeToggle,
              'red-4-color': !modeToggle,
            }"
          >
            {{
              modeToggle ? $t("system_card.active") : $t("system_card.inactive")
            }}
          </div>
          <BaseToggleSwitcher
            v-model="modeToggle"
            fillBg="background-success-default"
            disabledBg="v1-grey-3"
            fillCircle="v1-grey-1"
          />
        </div>
      </div>

      <div class="system-card-scroll">
        <div class="features-list" v-if="isShowingMarketingBullshit">
          <template v-for="(feature, index) in uiFeaturesList">
            <div
              class="qrw-paragraph-md"
              v-html="formatFeatureHtml(feature)"
              :key="feature"
            ></div>
            <BaseDivider
              color="v1-grey-8"
              :key="feature + '_divider'"
              v-if="index != uiFeaturesList.length - 1"
            />
          </template>
        </div>

        <div class="required-settings-list" :class="{
          off: !modeToggle
        }" v-else>
          <template v-for="(entry, key) in requiredSettings">
            <div class="required-setting" @click="openSettingEntry(entry)" :key="key">
              <div class="setting-icon">
                <BaseIcon
                  icon="check-verified"
                  class="white-color"
                  v-if="!entry.done"
                />
                <BaseIcon
                  icon="check-verified-filled"
                  class="green-5-color"
                  v-else
                />
              </div>
              <div class="setting-info w-100 gap-row-zero">
                <div
                  class="qrw-heading-xs bold"
                  :class="{
                    'white-color': !entry.done,
                    'green-5-color': entry.done,
                  }"
                >
                  {{ $t("mode_settings." + entry.key) }}
                </div>
                <div
                  class="qrw-paragraph-md-sm v1-grey-6-color"
                  :class="{
                    'v1-grey-6-color': !entry.done,
                    'green-5-color': entry.done,
                  }"
                >
                  {{ $t("mode_settings_hints." + entry.key) }}
                </div>
              </div>
            </div>

            <BaseDivider
              color="v1-grey-8"
              :key="key + 'divider'"
              v-if="key != requiredSettings.length - 1"
            />
          </template>
        </div>
      </div>
    </div>
    <SubscriptionStatusPanel @stateChanged="v => last_status_state = v" :mode="isProUser ? 'pro' : mode" />
  </div>
</template>

<script>
import SubscriptionStatusPanel from "@/components/subscriptions/SubscriptionStatusPanel.vue";

import CafeAPI from "@/api/cafe";

export default {
  props: {
    mode: String,
  },
  data() {
    return {
      last_status_state: 'promo'
    }
  },
  components: {
    SubscriptionStatusPanel,
  },
  methods: {
    formatFeatureHtml(feature) {
      return this.$t("new_systems_features." + feature)
        .replace(/<b>/g, '<span class="bold">')
        .replace(/<\/b>/g, "</span>");
    },
    openSettingEntry(entry) {
      const key = entry.key;
      const route =
        {
          menu: "menus",
          schedule: "general",
          tables: "halls",
        }[key] || this.mode;

      this.$router.push(route);
    }
  },
  computed: {
    cafe() {
      return this.$store.state.cafe;
    },
    subscription() {
      return this.$store.getters["subscriptions/all"][this.mode];
    },
    isProUser() {
      return this.$store.getters['subscriptions/isProUser'];
    },
    modeToggle: {
      get() {
        if (!this.cafe || !this.cafe.modes) return false;

        return this.cafe.modes[this.mode];
      },
      set(value) {
        this.$store.commit("toggleCafeMode", {
          mode: this.mode,
          value,
        });
        this.$store.dispatch("saveCafe");

        this.$store.dispatch("auditlog/write", {
          type: value ? "mode_enable" : "mode_disable",
          payload: {
            mode: this.mode,
          },
        });

        CafeAPI.setMode(this.cafe._id, this.mode, value).then((data) => {
          if (data.changed) {
            this.$store.dispatch("subscriptions/fetchAll", this.cafe._id);
          }
        });

        // if (!value && this.isSubscriptionActive) {
        //   this.cancel_reconfirm_modal = true;
        // }
      },
    },
    uiModeDisplayTitle() {
      return this.$store.getters["subscriptions/getModeDisplayTitle"](
        this.mode
      );
    },
    uiModeTitleColor() {
      return {
        inside: "white",
        pro: "white",
        delivery: "blue-1",
        preview: "white",
        preorder: "yellow-2",
        reserve: "green-1",
      }[this.mode];
    },
    isShowingMarketingBullshit() {
      return !this.isShowingToggle;
    },
    isShowingToggle() {
      // return (
      //   this.subscription &&
      //   (this.subscription.trial_period_until || this.subscription.paid_until)
      // );
      return !['promo', 'ended'].includes(this.last_status_state);
    },
    uiFeaturesList() {
      return (
        {
          preview: ["custom_page", "menu_translation", "pos_integration"],
          inside: [
            "inside_orders",
            "orders_panel",
            "online_payment",
            "online_tips",
            "ai",
            "custom_page",
            "pos_integration",
            "crm",
          ],
          delivery: [
            "delivery_orders",
            "sms_verification",
            "push_notifications",
            "orders_panel",
            "online_payment",
            "online_tips",
            "ai",
            "custom_page",
            "pos_integration",
            "crm",
          ],
          preorder: [
            "takeaway_orders",
            "sms_verification",
            "push_notifications",
            "orders_panel",
            "online_payment",
            "online_tips",
            "ai",
            "custom_page",
            "pos_integration",
            "crm",
          ],
          reserve: [
            "reserve_orders",
            "sms_verification",
            "push_notifications",
            "reserve_deposit",
            "reserve_calendar",
            "orders_panel",
            "online_payment",
            "online_tips",
            "ai",
            "custom_page",
            "pos_integration",
            "crm",
          ],
          pro: [
            "custom_page",
            "pro_inside_orders",
            "pro_takeaway_orders",
            "pro_delivery_orders",
            "pro_reserve_orders",
          ],
        }[this.mode] || []
      );
    },
    readyness() {
      const key =
        this.mode.substring(0, 1).toUpperCase() + this.mode.substring(1);

      const value = Object.assign({}, this.$store.getters[`ready${key}`]);
      const total = value.total;

      delete value.total;
      delete value.subtotal;

      return value;
    },
    requiredSettings() {
      const readynessEntries = Object.entries(this.readyness).map(e => ({
        done: e[1],
        key: e[0]
      }));

      readynessEntries.sort((a,b) => {
        return a.done ? 1 : -1;
      });

      return readynessEntries;
    },
  },
};
</script>

<style scoped>
.system-card {
  display: flex;
  height: 409px;
  width: 100%;
  max-height: 409px;
  min-height: 409px;
  overflow: hidden;
  border-radius: 8px;

  /* TODO: USE GAPS! */
  margin-bottom: 24px;

  transform: translateZ(0);
}

.system-info {
  width: 380px;
  min-width: 380px;

  display: flex;
  flex-direction: column;
}

.system-info-header {
  display: flex;
  flex-direction: column;
  row-gap: 4px;
  padding: 24px 24px 16px 24px;
  box-sizing: border-box;
}

.features-list {
  padding: 16px 24px 24px 24px;
  display: flex;
  flex-direction: column;
  row-gap: 16px;
  color: var(--v1-grey-2-color);
}

.system-card-scroll {
  height: 100%;
  display: flex;
  flex-direction: column;
  overflow-y: scroll;
  background: var(--v1-grey-9-color);
}

.toggle-box {
  display: flex;
  flex-direction: row;
  column-gap: 16px;
  align-items: center;
}

.required-settings-list {
  padding: 16px 24px 24px 24px;
  display: flex;
  flex-direction: column;
  row-gap: 16px;
  color: var(--v1-grey-2-color);
}

.required-setting {
  display: flex;
  flex-direction: row;
  column-gap: 8px;
  cursor: pointer;
}

.setting-icon {
  padding: 4px;
  box-sizing: border-box;
  display: flex;
  align-items: flex-start;
}

.required-settings-list.off {
  opacity: 0.52;
}
</style>
